// =====================================================
// DASHBOARD STATS — Profil des équipages + Corps de métier & études (RUN 4)
// =====================================================
// Section « Profil des équipages » + « Corps de métier & études » du cadrage
// 07/07. Fonctions pures, testables en vitest, sans dépendance React. Piloté par
// `DashboardFilters` (segment + édition), comme les autres sections.
//
// ── RÉALITÉ DES DONNÉES (probée sur desertours-dev `gcgqnhhijbcjzancaekm`) ────
//   • gender (contacts.gender, 00230) → source RÉELLE du H/F depuis le BED.
//     Tant que les BED ne sont pas remplis : majoritairement NULL → « indéterminé »
//     (VOULU, cf. brief : la vraie donnée vient du BED).
//   • birth_date                     → âge = N/A honnête tant que non renseigné
//   • school_id (→ schools.type)     → types d'études = best-effort
//   • socio_professional_category    → métiers = best-effort (champ libre)
//   • is_not_student                 → split étudiant / jeune actif (fiable) ✅
//
// DÉCISIONS :
//   1. Donut sexe F/M/Mixte : lit la colonne RÉELLE contacts.gender (00230).
//      Plus d'estimation prénom. gender NULL/autre → composition « indéterminée ».
//   2. Âge : calculé sur les birth_date disponibles ; N/A propre si trop peu.
//   3. Corps de métier & études : le split Étudiant / Jeune actif (fiable) passe
//      AVANT les métiers (note Maxime) ; métiers + études = best-effort documenté.

import { inWindow } from './periods';
import type { DateWindow, EditionRef, RawMemberContact, RawRows } from './types';

// ---------------------------------------------------------------------------
// Sexe DÉCLARÉ (BED) — plus d'inférence prénom (00230)
// ---------------------------------------------------------------------------
// Le sexe est désormais une DONNÉE RÉELLE : colonne `contacts.gender`, alimentée
// par le BED (retour client). On ne devine plus rien à partir du prénom.
// Fallback = 'indeterminé' quand gender est NULL/inconnu (pas d'estimation).

export type Sex = 'F' | 'M';

/**
 * Convertit la valeur DB `contacts.gender` (homme / femme / autre) en Sex F/M.
 * 'autre', NULL, ou toute autre valeur → `null` (→ composition « indéterminée »,
 * jamais deviné). C'est la SOURCE unique du H/F depuis 00230.
 */
export function genderToSex(gender: string | null | undefined): Sex | null {
  if (!gender) return null;
  const g = gender.trim().toLowerCase();
  if (g === 'femme') return 'F';
  if (g === 'homme') return 'M';
  return null;
}

// ---------------------------------------------------------------------------
// Scoping par plage de dates (RETOUCHE 1 : plus de segment — vue globale)
// ---------------------------------------------------------------------------

/**
 * Ensemble des crew_id de l'édition N ENTRÉS (created_at) dans la plage
 * sélectionnée. RETOUCHE 1 : le donut équipages devient une vue globale (plus de
 * recalcul par segment BPI/PI/ID/PR) ; le seul filtrage est temporel.
 */
export function editionCrewIds(
  rows: RawRows,
  edition: EditionRef,
  window: DateWindow
): Set<string> {
  return new Set(
    rows.crews
      .filter((c) => c.product_id === edition.id && inWindow(c.created_at, window))
      .map((c) => c.id)
  );
}

/** Membres (crew_members) des équipages de l'édition dans la plage. */
function membersInWindow(rows: RawRows, edition: EditionRef, window: DateWindow) {
  const crewIds = editionCrewIds(rows, edition, window);
  return (rows.crewMembers ?? []).filter((m) => crewIds.has(m.crew_id));
}

/** Contacts des membres, indexés par id. */
function memberContactMap(rows: RawRows): Map<string, RawMemberContact> {
  return new Map((rows.memberContacts ?? []).map((c) => [c.id, c]));
}

// ---------------------------------------------------------------------------
// 1. Répartition des équipages (donut Féminin / Masculin / Mixte)
// ---------------------------------------------------------------------------

export type CrewComposition = 'feminin' | 'masculin' | 'mixte' | 'indetermine';

export interface SexDistribution {
  feminin: number;
  masculin: number;
  mixte: number;
  /** Équipages dont au moins un membre a un genre non renseigné (NULL/autre). */
  indetermine: number;
  /** Nombre total d'équipages (dans le périmètre segment). */
  total: number;
  /** Combien d'équipages ont pu être classés F/M/Mixte (= total − indetermine). */
  classified: number;
}

/** Composition d'un équipage à partir des sexes inférés de ses membres. */
export function crewComposition(sexes: (Sex | null)[]): CrewComposition {
  if (sexes.length === 0) return 'indetermine';
  if (sexes.some((s) => s == null)) return 'indetermine';
  const hasF = sexes.includes('F');
  const hasM = sexes.includes('M');
  if (hasF && hasM) return 'mixte';
  if (hasF) return 'feminin';
  return 'masculin';
}

/**
 * Répartition des équipages par composition de sexe sur la plage sélectionnée
 * (vue globale). Le sexe est la DONNÉE RÉELLE contacts.gender (BED, 00230) ;
 * NULL/autre → équipage « indéterminé ».
 */
export function computeCrewSexDistribution(
  rows: RawRows,
  edition: EditionRef,
  window: DateWindow
): SexDistribution {
  const crewIds = editionCrewIds(rows, edition, window);
  const members = membersInWindow(rows, edition, window);
  const contacts = memberContactMap(rows);

  // Regrouper les membres par équipage.
  const byCrew = new Map<string, (Sex | null)[]>();
  crewIds.forEach((id) => byCrew.set(id, []));
  members.forEach((m) => {
    const contact = contacts.get(m.contact_id);
    const sex = genderToSex(contact?.gender);
    const arr = byCrew.get(m.crew_id);
    if (arr) arr.push(sex);
  });

  const dist: SexDistribution = {
    feminin: 0,
    masculin: 0,
    mixte: 0,
    indetermine: 0,
    total: byCrew.size,
    classified: 0,
  };
  byCrew.forEach((sexes) => {
    const comp = crewComposition(sexes);
    dist[comp] += 1;
  });
  dist.classified = dist.total - dist.indetermine;
  return dist;
}

// ---------------------------------------------------------------------------
// 2. Caractéristiques des ID — âge moyen + tranches d'âge
// ---------------------------------------------------------------------------

export interface AgeBracket {
  /** Libellé (18-21, 22-25, 26-30, 31+). */
  label: string;
  count: number;
}

export interface AgeProfile {
  /** Âge moyen (années) ou null si aucune date de naissance exploitable. */
  averageAge: number | null;
  /** Nombre de membres avec une date de naissance renseignée. */
  withBirthDate: number;
  /** Nombre total de membres dans le périmètre. */
  total: number;
  brackets: AgeBracket[];
  /** true si la donnée est trop partielle pour être fiable (< seuil). */
  partial: boolean;
}

const AGE_BRACKETS: { label: string; min: number; max: number }[] = [
  { label: '18-21', min: 18, max: 21 },
  { label: '22-25', min: 22, max: 25 },
  { label: '26-30', min: 26, max: 30 },
  { label: '31+', min: 31, max: 200 },
];

/** Âge en années révolues à la date `now` à partir d'une date de naissance ISO. */
export function ageFromBirthDate(birthIso: string, now: Date): number | null {
  const b = new Date(birthIso);
  if (isNaN(b.getTime())) return null;
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

/**
 * Profil d'âge de la cohorte (segment). `partial` = true si moins de
 * MIN_AGE_SAMPLE dates de naissance → l'UI affiche « donnée non renseignée ».
 * Sur le seed courant : 0 date de naissance → averageAge null, partial true.
 */
export function computeIdAgeProfile(
  rows: RawRows,
  edition: EditionRef,
  window: DateWindow,
  now: Date
): AgeProfile {
  const MIN_AGE_SAMPLE = 3;
  const members = membersInWindow(rows, edition, window);
  const contacts = memberContactMap(rows);

  const ages: number[] = [];
  members.forEach((m) => {
    const c = contacts.get(m.contact_id);
    if (c?.birth_date) {
      const age = ageFromBirthDate(c.birth_date, now);
      if (age != null) ages.push(age);
    }
  });

  const brackets: AgeBracket[] = AGE_BRACKETS.map((br) => ({
    label: br.label,
    count: ages.filter((a) => a >= br.min && a <= br.max).length,
  }));

  const averageAge =
    ages.length > 0 ? Math.round((ages.reduce((s, a) => s + a, 0) / ages.length) * 10) / 10 : null;

  return {
    averageAge,
    withBirthDate: ages.length,
    total: members.length,
    brackets,
    partial: ages.length < MIN_AGE_SAMPLE,
  };
}

// ---------------------------------------------------------------------------
// 3. Corps de métier & études (+ split Étudiant / Jeune actif prioritaire)
// ---------------------------------------------------------------------------

export interface StudentSplit {
  etudiant: number;
  jeuneActif: number;
  inconnu: number;
  total: number;
}

export interface TopItem {
  label: string;
  count: number;
}

export interface MetierEtudes {
  /** Split fiable Étudiant / Jeune actif (is_not_student). PRIORITAIRE (Maxime). */
  studentSplit: StudentSplit;
  /** Top 5 corps de métier (champ libre) — best-effort. */
  metiers: TopItem[];
  /** Combien de membres ont un métier renseigné. */
  metiersRenseignes: number;
  /** Top 5 types d'études (schools.type) — best-effort. */
  etudes: TopItem[];
  /** Combien de membres ont une école (donc un type d'études) renseignée. */
  etudesRenseignees: number;
  /** Total de membres dans le périmètre. */
  total: number;
}

/** Agrège un champ texte libre en Top N (clé normalisée, libellé du 1er vu). */
function topN(
  values: (string | null | undefined)[],
  n: number
): { items: TopItem[]; filled: number } {
  const counts = new Map<string, { label: string; count: number }>();
  let filled = 0;
  values.forEach((v) => {
    const label = (v ?? '').trim();
    if (!label) return;
    filled += 1;
    const key = label.toLowerCase();
    const cur = counts.get(key);
    if (cur) cur.count += 1;
    else counts.set(key, { label, count: 1 });
  });
  const items = [...counts.values()]
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .slice(0, n)
    .map((c) => ({ label: c.label, count: c.count }));
  return { items, filled };
}

export function computeMetierEtudes(
  rows: RawRows,
  edition: EditionRef,
  window: DateWindow
): MetierEtudes {
  const members = membersInWindow(rows, edition, window);
  const contacts = memberContactMap(rows);
  const schoolTypeById = new Map((rows.schools ?? []).map((s) => [s.id, s.type]));

  const memberContacts = members
    .map((m) => contacts.get(m.contact_id))
    .filter((c): c is RawMemberContact => !!c);

  // Split Étudiant / Jeune actif (fiable).
  const split: StudentSplit = {
    etudiant: 0,
    jeuneActif: 0,
    inconnu: 0,
    total: memberContacts.length,
  };
  memberContacts.forEach((c) => {
    if (c.is_not_student === true) split.jeuneActif += 1;
    else if (c.is_not_student === false) split.etudiant += 1;
    else split.inconnu += 1;
  });

  // Top 5 métiers (champ libre).
  const metierAgg = topN(
    memberContacts.map((c) => c.socio_professional_category),
    5
  );

  // Top 5 types d'études (via school_id → schools.type).
  const etudeAgg = topN(
    memberContacts.map((c) => (c.school_id ? schoolTypeById.get(c.school_id) : null)),
    5
  );

  return {
    studentSplit: split,
    metiers: metierAgg.items,
    metiersRenseignes: metierAgg.filled,
    etudes: etudeAgg.items,
    etudesRenseignees: etudeAgg.filled,
    total: memberContacts.length,
  };
}

// ---------------------------------------------------------------------------
// Agrégat de la section (une seule entrée pour le hook)
// ---------------------------------------------------------------------------

export interface ProfileData {
  sex: SexDistribution;
  age: AgeProfile;
  metierEtudes: MetierEtudes;
}

export interface ProfileParams {
  rows: RawRows;
  edition: EditionRef;
  /** Plage de dates sélectionnée (scope temporel des équipages). */
  window: DateWindow;
  now: Date;
}

export function computeProfile({ rows, edition, window, now }: ProfileParams): ProfileData {
  return {
    sex: computeCrewSexDistribution(rows, edition, window),
    age: computeIdAgeProfile(rows, edition, window, now),
    metierEtudes: computeMetierEtudes(rows, edition, window),
  };
}
